/**
 * Search, property filter and sort for the Einheiten list (Slice 8).
 *
 * WHAT CHANGED. The page carried search and the property select inline in one
 * bordered row. Sort joins them, and the row learns to stack: at phone width a
 * search field, a property select and a sort select cannot share a line without
 * one of them becoming unusable, so below `sm` the search takes the full width
 * and the two selects share the line under it.
 *
 * PURELY PRESENTATIONAL. It owns no state and no filtering: the page holds the
 * values and `unitListCollection` holds the rules. That keeps the one thing
 * worth protecting — which units match — testable without a DOM.
 *
 * `data-testid` seams rather than label text: CI runs a Babel plugin that wraps
 * dynamic JSX in extra elements (CLAUDE.md §8.4), and Slice 6 lost a CI run to
 * assertions that depended on where those element boundaries fell.
 */
import React from "react";
import { ChevronDown, Search } from "lucide-react";

import { UNIT_SORT_OPTIONS } from "./unitListCollection";

/** Shared look for the two selects, so they read as one control pair. */
const SELECT =
  "w-full cursor-pointer appearance-none rounded-[10px] border border-input bg-card py-2 pl-3 pr-8 font-['DM_Sans'] text-[12.5px] text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * A select plus the chevron `appearance-none` takes away.
 *
 * Suppressing the native arrow is what lets both selects match the search
 * field's border and radius, but it also removes the only thing marking them as
 * choosers. Side by side in bordered boxes they read as two buttons — so the
 * affordance is put back explicitly. `pointer-events-none` keeps the glyph out
 * of the way: clicking it still opens the select underneath.
 */
function SelectShell({ className = "", children }) {
  return (
    <div className={`relative min-w-0 flex-1 sm:flex-none ${className}`}>
      {children}
      <ChevronDown
        aria-hidden="true"
        className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
      />
    </div>
  );
}

export default function UnitsToolbar({
  searchTerm,
  onSearchChange,
  propertyFilter,
  onPropertyFilterChange,
  properties,
  sortId,
  onSortChange,
}) {
  return (
    <div
      data-testid="units-toolbar"
      className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center"
    >
      <label className="flex min-w-0 flex-1 items-center gap-[10px] rounded-[10px] border border-input bg-card px-[16px] py-[10px] focus-within:ring-2 focus-within:ring-ring">
        {/* The glyph inherits the row's muted colour instead of carrying its
            own, so it follows the theme like every other piece of chrome. */}
        <Search className="h-[14px] w-[14px] shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="sr-only">Einheiten durchsuchen</span>
        <input
          type="text"
          data-testid="units-search"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Suche nach Unit ID, Ort, PLZ, Adresse, Typ oder Belegung…"
          className="min-w-0 flex-1 border-none bg-transparent font-['DM_Sans'] text-[13px] text-foreground outline-none placeholder:text-muted-foreground"
        />
      </label>

      <div className="flex min-w-0 items-center gap-2">
        <SelectShell className="sm:max-w-[min(240px,32vw)]">
          <select
            data-testid="units-property-filter"
            aria-label="Liegenschaft"
            value={propertyFilter}
            onChange={(e) => onPropertyFilterChange(e.target.value)}
            className={SELECT}
          >
            <option value="">Alle Liegenschaften</option>
            {(properties || []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.title || p.id}
              </option>
            ))}
          </select>
        </SelectShell>

        <SelectShell>
          <select
            data-testid="units-sort"
            aria-label="Sortierung"
            value={sortId}
            onChange={(e) => onSortChange(e.target.value)}
            className={SELECT}
          >
            {UNIT_SORT_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </SelectShell>
      </div>
    </div>
  );
}
